import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { getDnsPlans } from '@/api/dns/getDnsPlans'
import ErrorCard from '@/components/ui/ErrorCard'
import Spinner from '@/components/ui/Spinner'
import { IconClose, IconSearch } from '@/res/icons'
import { ImageUztelecom } from '@/res/images'
import { NAMESPACE_WIDGET } from '@/res/namespaces'
import { ROUTE_WIDGET_FOR_DOMAIN } from '@/res/routes'
import { IDomainResponse } from '@/types/Domain'
import { IHostingPlan } from '@/types/Hosting'
import { numberWithSpaces } from '@/utils/numberWithSpaces'

interface Props {
  domain: string
  data?: IDomainResponse | null
  isError?: boolean
}

const DomainWidget: React.FC<Props> = ({ domain, isError, data }) => {
  const [domainLoading, setDomainLoading] = useState(false)
  const { t } = useTranslation(NAMESPACE_WIDGET)

  const [dnsPlan, setDnsPlan] = useState<null | IHostingPlan>(null)
  const router = useRouter()
  const color = router.query.color || '#f1f8ff'
  const [price, setPrice] = useState<string | null>(null)

  const { handleSubmit, register } = useForm({
    defaultValues: {
      domain,
    },
  })

  useEffect(() => {
    const handleRouteChange = () => setDomainLoading(true)
    const handleRouteComplete = () => setDomainLoading(false)
    router.events.on('routeChangeStart', handleRouteChange)
    router.events.on('routeChangeComplete', handleRouteComplete)

    return () => {
      router.events.off('routeChangeStart', () => handleRouteChange)
      router.events.off('routeChangeStart', () => handleRouteComplete)
    }
  })

  useEffect(() => {
    const getPlan = async () => {
      try {
        const res = await getDnsPlans()
        if (res.length) {
          const plan = res[0]
          setDnsPlan(plan)
          setPrice(plan.price)
        }
      } catch (err) {
        //eslint-disable-next-line
        console.log(err)
      }
    }
    if (!dnsPlan) {
      getPlan()
    }
  }, [dnsPlan])

  const onSubmit = handleSubmit(async ({ domain }) => {
    if (router.query.domain_name === domain) {
      return
    }
    if (!domain.includes('.')) {
      router.push(`${ROUTE_WIDGET_FOR_DOMAIN}/${domain}.uz`)
    } else {
      router.push(`${ROUTE_WIDGET_FOR_DOMAIN}/${domain}`)
    }
  })

  const onBusy = handleSubmit(async ({ domain }) => {
    if (!domain.includes('.')) {
      window.parent.location.href = `https://uzdc.uz/services/dns/${domain}.uz`
    } else {
      window.parent.location.href = `https://uzdc.uz/services/dns/${domain}`
    }
  })

  const isDomainFree = !!(!isError && !data && domain)

  const submitBtn = (
    <button
      type={isDomainFree ? 'button' : 'submit'}
      onClick={isDomainFree ? onBusy : undefined}
      className="px-4 group py-3 mt-4 w-full bg-[#ffee2d] hover:bg-[#228bd6] rounded-lg hover:opacity-90 transition-all"
    >
      <span className="font-bold group-hover:text-white text-base uppercase transition-colors">
        {isDomainFree ? t('register') : t('search')}
      </span>
    </button>
  )

  const content = isError ? (
    <ErrorCard text={t('domain_not_found')} />
  ) : data ? (
    <>
      <span className="!text-[#f71919]">{t('domain_is_busy')}</span>
      <button
        onClick={onBusy}
        className="mt-2 hover:text-white bg-[#ffee2d] hover:bg-[#228bd6] transition-all active:scale-x-95 flex items-center p-1 rounded"
      >
        {t('more')}
      </button>
    </>
  ) : domain ? (
    <div>
      <p className="!text-[#364364] font-semibold text-[32px] leading-[36px]">
        {domain}
      </p>
      <div className="text-base pt-2">
        <span className="text-[#00c084]">{t('domain_is_free')},</span>
        <span className="ml-1">
          {price ? numberWithSpaces(parseFloat(price)) + ' сум,' : ''}
        </span>{' '}
        {t('register_now')}
      </div>
    </div>
  ) : null
  return (
    <div
      className="max-w-[440px] w-full mx-auto"
      style={{
        backgroundColor: color as string,
      }}
    >
      <div className="max-w-md m-auto bg-white  rounded-xl flex flex-col items-center">
        <div
          className="rounded-t-xl w-full bg-no-repeat flex justify-center items-center text-white"
          style={{
            backgroundImage: `url(${ImageUztelecom.src})`,
          }}
        >
          <p className="flex justify-center  items-center text-base font-bold p-4 ">
            {t('register_domain')}
          </p>
        </div>
        <div className="w-full p-4 flex flex-col">
          <form onSubmit={onSubmit}>
            <div className="flex flex-col justify-center relative">
              <button type="submit">
                <IconSearch className="text-gray-600  absolute right-2 top-1/2 -translate-y-1/2" />
              </button>
              <input
                {...register('domain')}
                required
                placeholder="mysite.uz"
                autoCorrect="off"
                autoComplete="off"
                onKeyDown={(event) => {
                  if (event.keyCode == 13) {
                    event.preventDefault()
                    return false
                  }
                }}
                className="py-3 pl-2 pr-4  text-base border-[1px] outline-none border-[#d3e8f7] focus:border-primary rounded-md w-full placeholder:font-normal placeholder:text-base"
              />
              {domain ? (
                <button
                  type="button"
                  className="absolute right-9 top-1/2 -translate-y-1/2"
                >
                  <IconClose
                    onClick={() => {
                      router.push(ROUTE_WIDGET_FOR_DOMAIN)
                    }}
                    className="w-5 h-5 text-gray-600"
                  />
                </button>
              ) : null}
            </div>

            {content ? <div className="small:mt-5 mt-3">{content}</div> : null}
            <div className="flex justify-center">
              {domainLoading ? (
                <Spinner color="border-primary my-4" />
              ) : (
                submitBtn
              )}
            </div>

            <div className="flex justify-center">
              <a
                href="https://uzdc.uz/privacy-policy"
                style={{
                  margin: '15px 78px 3px',
                }}
                target="_blank"
                className="text-[14px] leading-4 text-center text-[#2db9e8]"
                rel="noreferrer"
              >
                {t('policy')}
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default DomainWidget
